import { formatDate, now } from "common";
import { Dayjs } from "dayjs";
import { Permission } from "types";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { DashboardContext, DashboardMode, DashboardScope } from "@/types/Dashboard";
import { createParams } from "@/utils/createParams";
import { getSeller } from "@/utils/getSeller";

export const getIncludedFields = (isGranted: (permission: Permission) => boolean) => {
    const include = ["capacity.max", "capacity.reserved"];

    include.push(
        "customerName",
        "event",
        "aggregatedBalance",
        "purchasedUnits",
        "noteCount",
        "guestStatusCounts",
        "voucherCount",
        "otaPurchaseCount",
        "guideCount",
        "statusCounts",
    );

    if (isGranted(Permission.WAIVERS_VIEW)) {
        include.push("waiverCount", "waiverParticipantCount");
    }

    if (isGranted(Permission.WAITLISTS_VIEW)) {
        include.push("waitlistCount");
    }

    if (isGranted(Permission.MODIFY_RESOURCES_EQUIPMENT)) {
        include.push("resourceUsageCount");
    }

    return include;
};

// Note: Update getTimeslotsQueryData in apps/seller/src/components/Dashboard/helper.ts if modifying any query param here.
export const getTimeslotParams = () => {
    const seller = getSeller();
    const { filters } = useDashboardFilterStore.getState();
    const isEquipmentMode = filters.mode === DashboardMode.EQUIPMENT;
    const isListAllView = filters.scope === DashboardScope.ALL && filters.mode === DashboardMode.LIST;
    const isDayMode = [DashboardMode.DAY, DashboardMode.EQUIPMENT, DashboardMode.TABLE].includes(filters.mode);
    const isTableView = filters.mode === DashboardMode.TABLE;

    return (start: Dayjs, end: Dayjs | null, options?: { products?: string[]; limit?: number }) => {
        const params: any = {
            // Fetch only current 1 day timeslot in All List view or Current Day view
            start: !end || isListAllView || isDayMode ? formatDate(start) : [formatDate(start), formatDate(end)],
            product: options?.products ?? filters.products.selected,
            limit: options?.limit ?? 100, // If there are more slots we should use `useAutoFetchCollection` to get more
        };

        if (start.isBefore(now()) && seller.preferences.booking.allowPastDates) {
            params.allowPastSlots = true;
        }

        const dashboardContext = dashboardContextMap[filters.scope];
        if (dashboardContext) {
            params.context = dashboardContext;
        }

        if (isEquipmentMode) {
            params.context = DashboardContext.EQUIPMENT;
        }

        if (isTableView) {
            params.context = DashboardContext.RESERVED;
        }

        // Query param rules for resources and guides:
        // "?" when `all == true && showUnassigned == true`
        // "?resources[exists]=true" when `all == true`
        // "?resources=empty" when `showUnassigned = true`
        // "?resources=123,456" when `selected.length > 0`
        // "?resources=empty,123,456"

        // Special query param rules for equipment mode:
        // "?resources[exists]=true" when `all == true && mode == "equipment"`
        // "?resources=123,456" when `selected.length > 0 && mode == "equipment"`

        const keys: Array<"guide" | "resource"> = ["guide", "resource"];

        for (const key of keys) {
            if (key === "resource" && isEquipmentMode) {
                if (filters[key].all) {
                    params[`${key}[exists]`] = true;
                } else {
                    params[key] = filters[key].selected;
                }
            } else if (filters[key].all && filters[key].showUnassigned) {
                // Nothing.
            } else if (filters[key].all) {
                params[`${key}[exists]`] = true;
            } else if (filters[key].showUnassigned) {
                params[key] = ["empty", ...filters[key].selected];
            } else {
                params[key] = filters[key].selected;
            }
        }

        return createParams(params);
    };
};

const dashboardContextMap = {
    [DashboardScope.RESERVED]: DashboardContext.RESERVED,
    [DashboardScope.AVAILABLE]: DashboardContext.AVAILABLE,
    [DashboardScope.WAITLIST]: DashboardContext.WAITLIST,
    [DashboardScope.ALL]: DashboardContext.ALL,
    [DashboardScope.ASSIGNED]: null,
};
